import { useState, useEffect, useCallback, useRef } from 'react';
import { devLog } from "../utils/devLog";
import { formatPaymentError } from '../utils/formatPaymentError';

/**
 * Custom hook for managing dLocal Smart Fields SDK
 * 
 * Handles SDK loading, card element creation, mounting, and tokenization.
 * Encapsulates all Smart Fields logic for reusability and testability.
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.apiKey - dLocal Smart Fields API key (public key from dLocal dashboard)
 * @param {string} config.locale - Locale code (e.g., 'en', 'es', 'pt')
 * @param {string} config.country - Country code (e.g., 'IN', 'BR', 'US')
 * @param {Object} config.styles - Style object for Smart Fields iframe
 * @param {boolean} config.isSandbox - Whether to use sandbox environment (default: false)
 * @param {Function} config.onReady - Callback when card element is ready
 * @param {Function} config.onError - Callback when error occurs
 * @param {Function} config.onBrandChange - Callback when card brand is detected
 * 
 * @returns {Object} Hook state and methods
 * 
 * @example
 * const { ready, brand, mount, createToken } = useDlocalSmartFields({
 *   apiKey: 'sandbox_public_key_xxx',
 *   locale: 'en',
 *   country: 'IN',
 *   isSandbox: true,
 *   styles: { base: { color: '#111' } },
 *   onReady: () => devLog('Ready'),
 *   onError: (err) => console.error(err)
 * });
 */
export const useDlocalSmartFields = ({
  apiKey,
  locale = 'en',
  country = 'US',
  styles = {},
  isSandbox = false,
  onReady,
  onError,
  onBrandChange,
}) => {
  const [dlocal, setDlocal] = useState(null);
  const [card, setCard] = useState(null);
  const [ready, setReady] = useState(false);
  const [brand, setBrand] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Use refs to track cleanup and prevent memory leaks
  const mountedRef = useRef(false);
  const cardRef = useRef(null);
  const scriptRef = useRef(null);
  const dlocalInstanceRef = useRef(null); // Store the dLocal instance for createToken
  
  // Store callbacks in refs to avoid recreating card element on callback changes
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const onBrandChangeRef = useRef(onBrandChange);
  
  // Update refs when callbacks change
  useEffect(() => {
    onReadyRef.current = onReady;
    onErrorRef.current = onError;
    onBrandChangeRef.current = onBrandChange;
  }, [onReady, onError, onBrandChange]);

  /**
   * Load dLocal Smart Fields SDK dynamically
   * Only loads when needed (dLocal card is an option)
   */
  useEffect(() => {
    if (!apiKey) {
      console.warn('useDlocalSmartFields: apiKey is required');
      return;
    }

    // Check if SDK is already loaded
    if (window.dlocal && typeof window.dlocal === 'function') {
      devLog('dLocal SDK already loaded');
      setDlocal(window.dlocal);
      setLoading(false);
      return;
    }

    // Prevent duplicate script loading
    if (scriptRef.current) {
      return;
    }

    const loadScript = () => {
      return new Promise((resolve, reject) => {
        // Use sandbox or production SDK based on environment
        const sdkUrl = isSandbox 
          ? 'https://js-sandbox.dlocal.com/' 
          : 'https://js.dlocal.com/';
        
        devLog(`🔄 Loading dLocal SDK: ${sdkUrl} (${isSandbox ? 'SANDBOX' : 'PRODUCTION'})`);
        
        const script = document.createElement('script');
        script.src = sdkUrl;
        script.async = false; // Load synchronously for predictable timing
        script.id = 'dlocal-smart-fields-sdk';
        
        script.onload = () => {
          devLog('SDK script loaded, waiting for initialization...');
          
          // Poll until window.dlocal is a function
          let attempts = 0;
          const maxAttempts = 30;
          
          const checkSDK = () => {
            attempts++;
            
            if (window.dlocal && typeof window.dlocal === 'function') {
              resolve(window.dlocal);
            } else if (attempts >= maxAttempts) {
              reject(new Error(`SDK failed to initialize. Type: ${typeof window.dlocal}`));
            } else {
              setTimeout(checkSDK, 50);
            }
          };
          
          setTimeout(checkSDK, 50);
        };
        
        script.onerror = () => {
          reject(new Error('Failed to load dLocal SDK'));
        };

        document.head.appendChild(script);
        scriptRef.current = script;
      });
    };

    setLoading(true);
    loadScript()
      .then((dlocalFn) => {
        setDlocal(dlocalFn);
      })
      .catch((err) => {
        console.error('❌ SDK loading error:', err);
        setError(err.message);
        onErrorRef.current?.(err.message);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      mountedRef.current = false;
    };
  }, [apiKey, isSandbox]);

  /**
   * Initialize card element when SDK is loaded
   */
  useEffect(() => {
    
    // USE window.dlocal DIRECTLY instead of state (avoids React serialization issues)
    if (!window.dlocal || !apiKey) {
      devLog('Skipping initialization - window.dlocal:', !!window.dlocal, 'apiKey:', !!apiKey);
      return;
    }

    // Prevent duplicate card element creation
    if (cardRef.current) {
      devLog('Card element already created');
      return;
    }

    try {
      
      // Verify SDK is ready
      if (typeof window.dlocal !== 'function') {
        throw new Error(`window.dlocal must be a function, got ${typeof window.dlocal}`);
      }
      
      // Step 1: Initialize dLocal with Smart Fields API key
      const dlocalInstance = window.dlocal(apiKey);
      
      if (!dlocalInstance) {
        throw new Error('dlocalInstance is null or undefined');
      }
      
      // Store instance for createToken
      dlocalInstanceRef.current = dlocalInstance;
      
      // Step 2: Create the fields instance
      const fields = dlocalInstance.fields({
        country: country || 'US',
        locale: locale || 'en',
      });
      
      if (!fields) {
        throw new Error('fields instance is null or undefined');
      }

      // Step 3: Create card element with FORCED VISIBLE STYLES      
      // FORCE visible black text - ignore any style customization for now
      const forcedStyles = {
        base: {
          color: '#000000',  // Pure black
          fontSize: '16px',
          fontFamily: 'Arial, sans-serif',
          fontWeight: '400',
          '::placeholder': {
            color: '#999999',
          },
        },
        invalid: {
          color: '#FF0000',  // Red for errors
        },
      };
      
      const cardElement = fields.create('card', { style: forcedStyles });
      
      devLog('Card element created');

      // Event: Card element is ready for input
      cardElement.on('ready', () => {
        setReady(true);
        onReadyRef.current?.();
      });

      // Event: Card brand detected (Visa, MC, Amex, etc.)
      cardElement.on('brand', (event) => {
        setBrand(event.brand);
        onBrandChangeRef.current?.(event.brand);
      });

      // Event: Validation error
      cardElement.on('error', (event) => {
        const errorMessage = formatPaymentError(
          event?.error?.message ||
          event?.message ||
          event?.error?.code ||
          'Card validation error'
        );
        setError(errorMessage);
        // onErrorRef.current?.(errorMessage);
      });

      // Event: Card data changed (for clearing errors)
      cardElement.on("change", (event) => {
          setError(null);
      });

      cardRef.current = cardElement;
      setCard(cardElement);

    } catch (err) {
      console.error('Error initializing card element:', err);
      const errorMsg = err.message || 'Failed to initialize card element';
      setError(errorMsg);
      onErrorRef.current?.(errorMsg);
    }

    // Cleanup on unmount or re-initialization
    return () => {
      if (cardRef.current) {
        try {
          cardRef.current.unmount?.();
          cardRef.current = null;
        } catch (err) {
          console.error('Error unmounting card element:', err);
        }
      }
      // Clear the dLocal instance ref
      dlocalInstanceRef.current = null;
    };
  }, [dlocal, apiKey, locale, country]); // dlocal state triggers when SDK is ready

  /**
   * Mount card element to a DOM element
   * 
   * @param {string|HTMLElement} target - CSS selector (e.g., '#card-element') or DOM element
   */
  const mount = useCallback((target) => {
    if (!cardRef.current) {
      console.warn('❌ Card element not initialized yet');
      return;
    }

    try {      
      // Get target element (handle both selector and DOM element)
      let targetElement;
      if (typeof target === 'string') {
        targetElement = document.querySelector(target);
        if (!targetElement) {
          throw new Error(`DOM element not found: ${target}`);
        }
      } else if (target instanceof HTMLElement) {
        targetElement = target;
      } else {
        throw new Error('Invalid target: must be a selector string or HTMLElement');
      }
      
      // Mount to the element directly
      cardRef.current.mount(targetElement);
      
      devLog('Card element mounted successfully');
      mountedRef.current = true;
    } catch (err) {
      console.error('❌ Error mounting card element:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        target,
        cardElement: !!cardRef.current
      });
      const errorMsg = err.message || 'Failed to mount card element';
      setError(errorMsg);
      onErrorRef.current?.(errorMsg);
    }
  }, []); // No dependencies needed - uses refs

  /**
   * Unmount card element from DOM
   */
  const unmount = useCallback(() => {
    if (!cardRef.current) return;

    try {
      devLog('Unmounting card element');
      cardRef.current.unmount();
      cardRef.current = null;
      dlocalInstanceRef.current = null; // Clear the instance
      setCard(null);
      setReady(false);
      setBrand(null);
    } catch (err) {
      console.error('Error unmounting card element:', err);
    }
  }, []);

  /**
   * Create token from card data
   * 
   * @param {Object} additionalData - Additional data for token creation
   * @param {string} additionalData.name - Cardholder name (required)
   * @returns {Promise<string>} Promise resolving to card_id token
   * 
   * @throws {Error} If tokenization fails
   * 
   * @example
   * const cardId = await createToken({ name: 'John Doe' });
   * devLog('Token:', cardId); // CID-xxx-xxx
   */
  const createToken = useCallback(async (additionalData = {}) => {
    if (!dlocalInstanceRef.current || !cardRef.current) {
      throw new Error('Smart Fields not initialized');
    }

    if (!ready) {
      throw new Error('Card element not ready');
    }

    if (!additionalData.name) {
      throw new Error('Cardholder name is required');
    }

    try {
      
      // Use the stored dLocal instance, not window.dlocal
      const result = await dlocalInstanceRef.current.createToken(cardRef.current, additionalData);
    
      
      if (result.error) {
        const errorMsg = result.error.message || 'Token creation failed';
        console.error('❌ Token creation error:', errorMsg);
        throw new Error(errorMsg);
      }

      // dLocal returns card_id (not token)
      const cardId = result.card_id || result.token || result.id;
      
      if (!cardId) {
        console.error('❌ No card_id in result:', result);
        throw new Error('Token creation did not return a card_id');
      }

      return cardId; // This is the card_id (CID-xxx-xxx-xxx)
      
    } catch (err) {
      console.error('Error creating token:', err);
      const errorMsg = err?.error?.message || err?.message || 'Failed to create token';
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  }, [ready]); // Using window.dlocal directly, only need ready flag

  /**
   * Reset error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Set mounted flag
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    // SDK instance
    dlocal,
    card, // Use state variable instead of ref so components can react to changes
    
    // State
    ready,
    brand,
    error,
    loading,
    
    // Methods
    mount,
    unmount,
    createToken,
    clearError,
  };
};

export default useDlocalSmartFields;

